import { useRef, useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { InputText } from "primereact/inputtext";
import { Button } from "primereact/button";
import { Dropdown } from "primereact/dropdown";
import PhoneInput from "react-phone-input-2";
import "react-phone-input-2/lib/style.css";
import "../styles/CreateTerminalPage.css";
import {
  FaStore,
  FaCreditCard,
  FaLocationDot,
  FaFingerprint,
  FaUserCheck,
  FaWifi,
  FaLayerGroup,
  FaCity,
  FaMapLocationDot,
  FaGlobe,
  FaServer,
  FaKeyboard,
} from "react-icons/fa6";
import { Formik, Form, Field, ErrorMessage } from "formik";
import * as Yup from "yup";
import { createTerminal, getAllMerchants, updateTerminal } from "../api/api";
import { CITY_LIST, MCC_LIST, POSTERMINAL } from "../utils/constants";
import { toDropdownOptions } from "../utils/enumUtils";
const cityOptions = CITY_LIST.map((city) => ({
  label: city,
  value: city,
}));

const mccOptions = MCC_LIST.map((mcc) => ({
  label: `${mcc.code} - ${mcc.name}`,
  value: mcc.code,
}));
const TERMINAL_TYPE_OPTIONS = [
  { label: "POS", value: "POS" },
  { label: "SoftPOS", value: "SOFTPOS" },
  { label: "Transit All", value: "TRANSIT_ALL" },
];
// ✅ Validation Schema
const validationSchema = Yup.object().shape({
  merchantId: Yup.string().required("Merchant ID is required"),
  terminalId: Yup.string()
    .matches(/^[0-9]{1,8}$/, "Terminal ID must be 1–8 digits")
    .min(8, "Terminal ID cannot less then 8 digits")
    .required("Terminal ID is required"),
  phoneNumber: Yup.string()
    .test("is-valid-phone", "Enter a valid phone number", (value) => {
      return value && value.replace(/\D/g, "").length > 4;
    })
    .required("Phone number is required"),

  environment: Yup.string()
    .oneOf(["TEST", "PRODUCTION"], "Invalid environment")
    .required("Environment is required"),

  transactionType: Yup.array()
    .of(
      Yup.string().oneOf(
        [
          "SALES",
          "VOID",
          "REFUND",
          "SETTLEMENT",
          "PRE AUTH",
          "PRE AUTH COMPLETION",
          "TIP",
          "TIP COMPLETION",
          "SALES ADJUST",
          "BALANCE INQUIRY",
          "QR PAYMENTS",
          "KISAN CARD",
        ],
        "Invalid Card",
      ),
    )
    .min(1, "At least one Transaction Type is required")
    .required("Transaction Type is required"),
  MCC: Yup.string().required("Merchant Category Code is required"),
  city: Yup.string().required("City is required"),
  province: Yup.string().required("Province is required"),
  terminalType: Yup.string().required("Terminal Type is required"),
  cards: Yup.array()
    .of(
      Yup.string().oneOf(
        ["VC", "MC", "JCB", "UPI", "PURE", "DIN"],
        "Invalid Card",
      ),
    )
    .min(1, "At least one card is required")
    .required("Card is required"),
  // serialNumber: Yup.string().required("Serial number is required"),
});

// ✅ Default Form Values
const INITIAL_FORM = {
  merchantId: "",
  terminalId: "",
  terminalType: "",
  phoneNumber: "92",

  MCC: "",
  city: "",
  province: "",

  transactionType: [],
  environment: "",
  cards: [],
};
const CARD_OPTIONS = [
  { label: "Visa Card", value: "VC" },
  { label: "Master Card", value: "MC" },
  { label: "Pay Pak", value: "PURE" },
  { label: "Union Pay", value: "UPI" },
  { label: "Japenese Credit Bureau", value: "JCB" },
  { label: "Diners", value: "DIN" },
];
const ENVIRONMENT_OPTIONS = [
  { label: "Test", value: "TEST" },
  { label: "Production", value: "PRODUCTION" },
];
const PROVINCE_OPTIONS = [
  { label: "Punjab", value: "Punjab" },
  { label: "Sindh", value: "Sindh" },
  { label: "Khyber Pakhtunkhwa", value: "Khyber Pakhtunkhwa" },
  { label: "Balochistan", value: "Balochistan" },
  {
    label: "Islamabad Capital Territory",
    value: "Islamabad Capital Territory",
  },
  { label: "Gilgit-Baltistan", value: "Gilgit-Baltistan" },
  { label: "Azad Jammu and Kashmir", value: "Azad Jammu and Kashmir" },
];
export default function CreateTerminalPage() {
  const [loading, setLoading] = useState(false);
  const [merchantLoading, setMerchantLoading] = useState(false);
  const [merchants, setMerchants] = useState([]);
  const navigate = useNavigate();
  const location = useLocation();

  // ✅ Extract terminal and mode from route state
  const { terminal, mode } = location.state || {};
  const isViewMode = mode === "view";
  const isEditMode = mode === "edit";

  // ✅ Fetch merchants for dropdown
  useEffect(() => {
    const fetchData = async () => {
      try {
        setMerchantLoading(true);
        const res = await getAllMerchants();
        if (res?.data) setMerchants(res.data);
      } catch (err) {
        console.error("Error fetching merchants:", err);
      } finally {
        setMerchantLoading(false);
      }
    };
    fetchData();
  }, []);
  const getTerminalDefaults = (terminalType) => {
    if (terminalType === "POS") {
      return {
        attended: "ATTENDED",
        cardholderActivated: "MPOS_ACCEPTANCE_DEVICE",
        cardPresenceCapability: "CARD_PRESENT",
        inputCapability: "CHIP_AND_KEY_ENTRY_AND_MAGNETIC_STRIPE_AND_RFID",
        location: "MERCHANT_TERMINAL_ON_PREMISES",
        onlineReasonCode: "FORCED_BY_MERCHANT",
        panEntryMode: "CHIP",
        pinEntryCapability: "PIN_SUPPORTED",
      };
    }

    if (terminalType === "SOFTPOS") {
      return {
        attended: "ATTENDED",
        cardholderActivated: "MPOS_ACCEPTANCE_DEVICE",
        cardPresenceCapability: "CARD_PRESENT",
        inputCapability: "CONTACTLESS_CHIP",
        location: "MERCHANT_TERMINAL_ON_PREMISES",
        onlineReasonCode: "FORCED_BY_MERCHANT",
        panEntryMode: "CONTACTLESS",
        pinEntryCapability: "PIN_SUPPORTED",
      };
    }

    if (terminalType === "TRANSIT_ALL") {
      return {
        attended: "ATTENDED",
        cardholderActivated: "NOT_CARDHOLDER_ACTIVATED",
        cardPresenceCapability: "CARD_PRESENT",
        inputCapability: "CONTACTLESS_CHIP",
        location: "MERCHANT_TERMINAL_ON_PREMISES",
        onlineReasonCode: "FORCED_BY_MERCHANT",
        panEntryMode: "CONTACTLESS",
        pinEntryCapability: "PIN_NOT_SUPPORTED",
      };
    }

    return {};
  };
  // ✅ Handle submit (create or update)
  const handleSubmit = async (values, { resetForm }) => {
    if (isViewMode) return;
    // setLoading(true);
    try {
      const terminalDefaults = getTerminalDefaults(values.terminalType);
      console.log("values", JSON.stringify(values, null, 2));
      const payload = {
        ...values,
        ...terminalDefaults,

        TID: values.terminalId,
        MID: values.merchantId,
        PhoneNumber: `+${values.phoneNumber}`,

        terminalType: values.terminalType,

        // ✅ lane will be city
        lane: values.city,
      };

      const res = isEditMode
        ? await updateTerminal(payload)
        : await createTerminal(payload);

      if (res?.isSuccess) {
        alert(
          isEditMode
            ? "Terminal updated successfully!"
            : "Terminal created successfully!",
        );

        navigate("/dashboard/terminalslist");
      } else {
        alert(res?.message || "Failed to save terminal!");
      }
    } catch (err) {
      console.error("Terminal Save Error:", err);
      alert(err);
    } finally {
      setLoading(false);
    }
  };

  // ✅ Initial values based on mode
  const formInitialValues =
    isEditMode || isViewMode
      ? {
          ...INITIAL_FORM,
          merchantId: terminal?.merchantId || terminal?.MID || "",
          terminalId: terminal?.TID || "",
          phoneNumber: terminal?.PhoneNumber || "92",
          // attended: terminal?.attended || "",
          // cardholderActivated: terminal?.cardholderActivated || "",
          // cardPresenceCapability: terminal?.cardPresenceCapability || "",
          // inputCapability: terminal?.inputCapability || "",
          // location: terminal?.location || "",
          // onlineReasonCode: terminal?.onlineReasonCode || "",
          // panEntryMode: terminal?.panEntryMode || "",
          // pinEntryCapability: terminal?.pinEntryCapability || "",
          transactionType: terminal?.transactionType || "",
          environment: terminal?.environment || "",

          lane: terminal?.lane || "",
          cards: terminal?.cards || [],
          MCC: terminal?.MCC || "",
          city: terminal?.city || "",
          province: terminal?.province || "",
          geoLocation: {
            lat: terminal?.geoLocation?.lat || "",
            long: terminal?.geoLocation?.long || "",
          },
          terminalType: terminal?.terminalType || "",
          // serialNumber: terminal?.serialNumber || "",
        }
      : INITIAL_FORM;

  if (merchantLoading) {
    return (
      <div className="create-terminal-page">
        <p className="loader">Loading...</p>
      </div>
    );
  }

  return (
    <div className="create-terminal-page">
      <div className="create-terminal-container">
        <Formik
          initialValues={formInitialValues}
          validationSchema={isViewMode ? null : validationSchema}
          onSubmit={handleSubmit}
          enableReinitialize
        >
          {({ values, setFieldValue }) => (
            <Form className="create-terminal-form">
              {/* Merchant ID */}
              <div className="input-with-icon">
                <FaStore className="input-icon" />
                <Dropdown
                  name="merchantId"
                  value={values.merchantId}
                  options={merchants.map((m) => ({
                    label: m.MID,
                    value: m.MID,
                  }))}
                  disabled={isViewMode || isEditMode}
                  onChange={(e) => setFieldValue("merchantId", e.value)}
                  placeholder="Select Merchant"
                  className="onboard-input w-full"
                  filter
                  showClear
                />
              </div>
              <ErrorMessage
                name="merchantId"
                component="div"
                className="error-text"
              />

              {/* Terminal ID */}
              <div className="input-with-icon">
                <FaCreditCard className="input-icon" />
                <Field
                  name="terminalId"
                  as={InputText}
                  maxLength={8}
                  placeholder="Terminal ID"
                  className="onboard-input"
                  disabled={isViewMode || isEditMode}
                  onChange={(e) => {
                    const onlyDigits = e.target.value.replace(/\D/g, "");
                    setFieldValue("terminalId", onlyDigits);
                  }}
                />
              </div>
              <ErrorMessage
                name="terminalId"
                component="div"
                className="error-text"
              />
              <div className="input-with-icon">
                <FaCreditCard className="input-icon" />
                <Dropdown
                  name="terminalType"
                  value={values.terminalType}
                  options={TERMINAL_TYPE_OPTIONS}
                  onChange={(e) => setFieldValue("terminalType", e.value)}
                  placeholder="Select Terminal Type"
                  className="onboard-input w-full"
                  disabled={isViewMode}
                  filter
                  showClear
                />
              </div>
              <ErrorMessage
                name="terminalType"
                component="div"
                className="error-text"
              />
              <div className="input-with-icon">
                <FaGlobe className="input-icon" />
                <Dropdown
                  name="MCC"
                  value={values.MCC}
                  options={mccOptions}
                  onChange={(e) => setFieldValue("MCC", e.value)}
                  placeholder="Select MCC"
                  className="onboard-input w-full"
                  disabled={isViewMode}
                  filter
                  showClear
                />
              </div>

              <div className="input-with-icon">
                <FaCity className="input-icon" />
                <Dropdown
                  name="city"
                  value={values.city}
                  options={cityOptions}
                  onChange={(e) => setFieldValue("city", e.value)}
                  placeholder="Select City"
                  className="onboard-input w-full"
                  disabled={isViewMode}
                  filter
                  showClear
                />
              </div>

              <div className="input-with-icon">
                <FaMapLocationDot className="input-icon" />
                <Dropdown
                  name="province"
                  value={values.province}
                  options={PROVINCE_OPTIONS}
                  onChange={(e) => setFieldValue("province", e.value)}
                  placeholder="Select Province"
                  className="onboard-input w-full"
                  disabled={isViewMode}
                  filter
                  showClear
                />
              </div>
              {/* Phone Number */}
              <div className="input-with-icon">
                <PhoneInput
                  country="pk"
                  value={values.phoneNumber}
                  onChange={(value) =>
                    setFieldValue("phoneNumber", value.replace(/\D/g, ""))
                  }
                  placeholder="Enter phone number"
                  containerClass="create-terminal__phone"
                  inputClass="create-terminal__phone-input"
                  enableSearch
                  countryCodeEditable={false}
                  disabled={isViewMode || isEditMode}
                />
              </div>
              <ErrorMessage
                name="phoneNumber"
                component="div"
                className="error-text"
              />

              {/* Environment */}
              <div className="input-with-icon">
                <FaServer className="input-icon" />
                <Dropdown
                  name="environment"
                  value={values.environment}
                  options={ENVIRONMENT_OPTIONS}
                  onChange={(e) => setFieldValue("environment", e.value)}
                  placeholder="Select Environment"
                  className="onboard-input w-full"
                  disabled={isViewMode}
                  filter
                  showClear
                />
              </div>
              <ErrorMessage
                name="environment"
                component="div"
                className="error-text"
              />

              {/* Lane */}
             
              <div className="roles-section">
                <div className="roles-header">
                  <label className="roles-label"> Cards Scheme</label>
                </div>
                <div className="roles-list">
                  {CARD_OPTIONS.map((card) => (
                    <label key={card.value} className="role-checkbox">
                      <input
                        type="checkbox"
                        className="custom-checkbox"
                        name="roles"
                        value={card.value}
                        checked={values.cards.includes(card.value)}
                        disabled={isViewMode}
                        onChange={(e) => {
                          const isChecked = e.target.checked;

                          let updatedCards = [...values.cards];

                          if (isChecked) {
                            updatedCards.push(card.value);
                          } else {
                            updatedCards = updatedCards.filter(
                              (val) => val !== card.value,
                            );
                          }

                          setFieldValue("cards", updatedCards);
                        }}
                      />
                      <span>{card.label}</span>
                    </label>
                  ))}
                </div>
                <ErrorMessage
                  name="cards"
                  component="div"
                  className="error-text"
                />
              </div>
              <div className="roles-section">
                <div className="roles-header">
                  <label className="roles-label"> Transaction Type</label>
                </div>
                <div className="roles-list">
                  {POSTERMINAL.TRANSACTION_TYPE.map((type) => (
                    <label key={type.value} className="role-checkbox">
                      <input
                        type="checkbox"
                        className="custom-checkbox"
                        name="transactionType"
                        value={type.value}
                        checked={values.transactionType.includes(type.value)}
                        disabled={isViewMode}
                        onChange={(e) => {
                          const isChecked = e.target.checked;

                          let updatedCards = [...values.transactionType];

                          if (isChecked) {
                            updatedCards.push(type.value);
                          } else {
                            updatedCards = updatedCards.filter(
                              (val) => val !== type.value,
                            );
                          }

                          setFieldValue("transactionType", updatedCards);
                        }}
                      />
                      <span>{type.label}</span>
                    </label>
                  ))}
                </div>
                <ErrorMessage
                  name="transactionType"
                  component="div"
                  className="error-text"
                />
              </div>

              {/* Buttons */}

              {!isViewMode && (
                <Button
                  type="submit"
                  label={
                    loading ? "Saving..." : isEditMode ? "Update" : "Create"
                  }
                  disabled={loading}
                  className="create-terminal__submit"
                />
              )}
            </Form>
          )}
        </Formik>
      </div>
    </div>
  );
}
